import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, ArrowLeft, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/layouts/AdminLayout";
import { EquipmentEditor, EquipmentReadList, toPayload } from "@/components/EquipmentLines";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FormDialog } from "@/components/FormDialog";
import { GpsUnitsEditor } from "@/components/gps/GpsUnitsEditor";
import { reserveMove } from "@/components/gps/InventoryDevicePick";
import { FinanceSections } from "@/components/wo/FinanceSections";
import { ExtraServiceDrafts, draftPayload } from "@/components/wo/ExtraServiceDrafts";
import { gpsFlow, toUnitPayload } from "@/lib/gps";
import { movementOptions } from "@/lib/movements";
import { TechnicianPicker } from "@/components/technicians/TechnicianPicker";
import { useList } from "@/lib/technicians";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { clientFields, projectFields, siteFields } from "@/lib/forms";
import { MANUAL_STATUSES, STATUS } from "@/lib/constants";

const NONE = "__none";
const EMPTY = { client_id: "", project_id: "", site_id: null, business_unit_code: "", service_type_code: "", service_date: "", priority: "media", service_address: "", contact_name: "", contact_phone: "", contact_email: "", environment: "normal", scope: "", notes: "", technician_id: null, scheduled_date: "", scheduled_time: "", status: null, warranty_months_selected: null, checklist_template_id: null, zone_id: null, specialty_code: null, technician_rate_id: null };

const Field = ({ label, children, className }) => (
  <div className={`space-y-1.5 ${className || ""}`}>
    <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
    {children}
  </div>
);

const Pick = ({ value, onChange, options, placeholder, testid, nullable }) => (
  <Select value={value || NONE} onValueChange={(v) => onChange(v === NONE ? null : v)}>
    <SelectTrigger className="h-11" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>
      {nullable && <SelectItem value={NONE}>{nullable}</SelectItem>}
      {!nullable && !value && <SelectItem value={NONE} disabled>{placeholder}</SelectItem>}
      {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
    </SelectContent>
  </Select>
);

const ClearableInput = ({ type, value, onChange, testid }) => (
  <div className="relative">
    <Input className={cn("h-11 pr-10", !value && "text-muted-foreground")} type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={`${testid}-input`} />
    {value ? (
      <button type="button" onClick={() => onChange("")} title="Quitar" className="absolute right-2 top-2.5 flex h-6 w-6 items-center justify-center text-muted-foreground hover:text-foreground" data-testid={`${testid}-clear-button`}><X className="h-4 w-4" /></button>
    ) : <span className="pointer-events-none absolute right-10 top-3 text-xs text-amber-400" data-testid={`${testid}-unscheduled`}>Sin programar</span>}
  </div>
);

const Section = ({ n, title, children }) => (
  <section className="fade-up rounded-xl border border-border bg-card/40 p-5">
    <h2 className="mb-4 flex items-center gap-3 font-display text-lg font-semibold">
      <span className="font-mono text-xs text-primary">{n}</span>{title}
    </h2>
    {children}
  </section>
);

export default function WorkOrderForm() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { meta } = useAuth();
  const [f, setF] = useState({ ...EMPTY, client_id: sp.get("client") || "", project_id: sp.get("project") || "" });
  const [lines, setLines] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [techs, setTechs] = useState([]);
  const [zones] = useList("/zones");
  const [dlg, setDlg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [folio, setFolio] = useState(null);
  const [modality, setModality] = useState(sp.get("proyecto") || sp.get("project") ? "proyecto" : "normal");
  const [gps, setGps] = useState(null);
  const [units, setUnits] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [wopt, setWopt] = useState(null);
  const [extras, setExtras] = useState([]);
  const prefilled = useRef(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/users/technicians").then((r) => setTechs(r.data));
    api.get("/settings/checklists").then((r) => setTemplates(r.data));
    if (!id) return;
    api.get(`/work-orders/${id}`).then(({ data: w }) => {
      setFolio(w.folio);
      setF({ ...EMPTY, ...w, site_id: w.site?.id || null, contact_name: w.contact?.name, contact_phone: w.contact?.phone, contact_email: w.contact?.email, scheduled_date: w.scheduled_date || "", scheduled_time: w.scheduled_time || "", status: null, checklist_template_id: w.checklist?.template_id || "none", zone_id: w.zone_id || null, specialty_code: w.assignment?.specialty_code || null, technician_rate_id: null });
      setModality(w.project_id ? "proyecto" : "normal");
      setGps(w.extensions?.gps || {});
      setLines(w.equipment);
    });
  }, [id]);

  useEffect(() => {
    if (!f.client_id) return setProjects([]);
    api.get("/projects", { params: { client_id: f.client_id } }).then((r) => setProjects(r.data));
  }, [f.client_id]);

  useEffect(() => {
    if (id || prefilled.current || !f.project_id || !projects.find((p) => p.id === f.project_id)) return;
    prefilled.current = true;
    chooseProject(f.project_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects]);

  useEffect(() => {
    if (!f.business_unit_code || !f.service_type_code) return setWopt(null);
    api.get("/work-orders/warranty-options", { params: { business_unit_code: f.business_unit_code, service_type_code: f.service_type_code } }).then((r) => setWopt(r.data));
  }, [f.business_unit_code, f.service_type_code]);

  const chooseClient = (v) => {
    const c = clients.find((x) => x.id === v);
    setF((s) => ({ ...s, client_id: v, project_id: "", site_id: null, ...(modality === "normal" ? { service_address: c?.address || "", contact_name: c?.contact_name || "", contact_phone: c?.phone || "", contact_email: c?.email || "" } : {}) }));
  };

  const chooseProject = (pid) => {
    const p = projects.find((x) => x.id === pid);
    const c = clients.find((x) => x.id === f.client_id);
    setF((s) => ({ ...s, project_id: pid, business_unit_code: p?.business_unit_code || s.business_unit_code, environment: p?.environment || "normal", zone_id: p?.zone_id || s.zone_id,
      service_address: p?.address || c?.address || s.service_address, contact_name: s.contact_name || c?.contact_name || "", contact_phone: s.contact_phone || c?.phone || "", contact_email: s.contact_email || c?.email || "" }));
  };

  const chooseSite = (sid) => {
    const st = sites.find((x) => x.id === sid);
    setF((s) => ({ ...s, site_id: sid, ...(st ? { service_address: st.address || "", contact_name: st.contact_name || "", contact_phone: st.phone || "" } : {}) }));
  };

  const submit = async () => {
    const normal = modality === "normal";
    if ((normal ? !f.client_id : !f.project_id) || !f.business_unit_code || !f.service_type_code)
      return toast.error(normal ? "Selecciona cliente, unidad y tipo de servicio" : "Selecciona proyecto, unidad de negocio y tipo de servicio");
    setSaving(true);
    const { client_id, ...body } = f;
    if (!body.status) delete body.status;
    if (normal) { delete body.project_id; body.client_id = client_id; }
    if (!body.checklist_template_id) delete body.checklist_template_id;
    const isGps = f.business_unit_code === "gps";
    const flow = gpsFlow(f.service_type_code);
    if (isGps && !id && flow.install && units.some((u) => !u.plan)) { setSaving(false); return toast.error("Selecciona el plan de cada unidad GPS"); }
    if (isGps && !id && flow.remove && units.some((u) => !u.target)) { setSaving(false); return toast.error("Selecciona el equipo GPS a retirar de cada unidad"); }
    if (isGps && !id && flow.reinstall && units.some((u) => !u.source_device)) { setSaving(false); return toast.error("Selecciona el equipo en resguardo TSSMX a reinstalar en cada unidad"); }
    if (!id && extras.some((s) => !s.business_unit_code || !s.service_type_code)) { setSaving(false); return toast.error("Selecciona unidad de negocio y tipo de servicio en cada servicio adicional, o quítalo"); }
    try {
      if (id) {
        await api.patch(`/work-orders/${id}`, body);
        await api.put(`/work-orders/${id}/equipment`, { lines: toPayload(lines) });
        toast.success("OS actualizada");
        nav(`/admin/os/${id}`);
      } else {
        const { data } = await api.post("/work-orders", { ...body, equipment: toPayload(lines), gps: isGps ? { units: units.map(toUnitPayload) } : undefined, extra_services: draftPayload(extras) });
        const picks = isGps ? units.flatMap((u) => u.devices.filter((d) => d.inv_pick && d.inv_pick.serial === d.imei).map((d) => d.inv_pick)) : [];
        const failed = [];
        for (const p of picks) await reserveMove("reserva", p, data.id).catch(() => failed.push(p.serial));
        if (failed.length) toast.warning(`No se pudo reservar el IMEI ${failed.join(", ")}; revísalo en la OS.`);
        toast.success(`OS ${data.folio} creada`);
        nav(`/admin/os/${data.id}`);
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const saveClient = async (form) => {
    const { data } = await api.post("/clients", form);
    setClients((c) => [...c, data].sort((a, b) => a.name.localeCompare(b.name)));
    setF((s) => ({ ...s, client_id: data.id, project_id: "" }));
    toast.success("Cliente creado");
  };
  const saveProject = async (form) => {
    const { data } = await api.post("/projects", { ...form, client_id: f.client_id });
    setProjects((p) => [data, ...p]);
    setF((s) => ({ ...s, project_id: data.id, business_unit_code: data.business_unit_code, environment: data.environment, zone_id: data.zone_id || s.zone_id, service_address: data.address || s.service_address }));
    toast.success("Proyecto creado");
  };

  const saveSite = async (form) => {
    const { data } = await api.post(`/clients/${f.client_id}/sites`, form);
    setClients((cs) => cs.map((c) => (c.id === f.client_id ? { ...c, sites: [...(c.sites || []), data] } : c)));
    setF((s) => ({ ...s, site_id: data.id, service_address: data.address || "", contact_name: data.contact_name || "", contact_phone: data.phone || "" }));
    toast.success("Sitio creado");
  };

  const sites = clients.find((c) => c.id === f.client_id)?.sites || [];
  const planned = projects.find((p) => p.id === f.project_id)?.planned_equipment || [];
  const currentTechName = techs.find((t) => t.id === f.technician_id)?.name || f.technician_name;
  const isGps = f.business_unit_code === "gps";
  const schedule = (
        <Section n={isGps ? "03" : "04"} title="Programación y asignación">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Técnico asignado" className="sm:col-span-2">
              <TechnicianPicker query={f} value={f.technician_id} specialty={f.specialty_code} rateId={f.technician_rate_id} currentName={currentTechName}
                onChange={(v) => setF((s) => ({ ...s, ...v }))} />
            </Field>
            <Field label="Estado (opcional)"><Pick value={f.status} onChange={(v) => set("status", v)} nullable="Automático" testid="wo-status-select" options={MANUAL_STATUSES.map((s) => ({ value: s, label: STATUS[s].label }))} /></Field>
            <Field label="Fecha del servicio (solo si ya se realizó)"><ClearableInput type="date" value={f.service_date || ""} onChange={(v) => set("service_date", v)} testid="wo-service-date" /></Field>
            <Field label="Fecha programada (opcional)"><ClearableInput type="date" value={f.scheduled_date} onChange={(v) => set("scheduled_date", v)} testid="wo-date" /></Field>
            <Field label="Hora programada (opcional)"><ClearableInput type="time" value={f.scheduled_time} onChange={(v) => set("scheduled_time", v)} testid="wo-time" /></Field>
          </div>
          <p className="mt-3 text-xs text-muted-foreground" data-testid="wo-schedule-hint">
            {f.scheduled_date && f.scheduled_time ? "Programación completa." : "Sin fecha y hora completas la OS queda como \"Pendiente de programación\"."}
          </p>
        </Section>
  );

  return (
    <div className="max-w-4xl">
      <Button variant="ghost" size="sm" className="mb-2 -ml-2 text-muted-foreground" onClick={() => nav(-1)} data-testid="back-button"><ArrowLeft className="mr-1 h-4 w-4" />Volver</Button>
      <PageHeader eyebrow={folio || "Nueva orden"} title={id ? "Editar orden de servicio" : "Nueva orden de servicio"} />
      <div className="space-y-5">
        <Section n="01" title={modality === "normal" ? "Cliente" : "Cliente y proyecto"}>
          {!id && (
            <div className="mb-4 inline-flex rounded-lg border border-border p-1" data-testid="wo-modality-toggle">
              {[["normal", "Orden de Servicio"], ["proyecto", "OS por Proyecto"]].map(([v, l]) => (
                <button key={v} type="button" onClick={() => { setModality(v); setF((s) => ({ ...s, project_id: "" })); }} data-testid={`wo-modality-${v}`}
                  className={cn("rounded-md px-4 py-2 text-sm font-medium", modality === v ? "bg-primary text-primary-foreground" : "text-muted-foreground")}>{l}</button>
              ))}
            </div>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Cliente *">
              <div className="flex gap-2">
                <div className="flex-1"><Pick value={f.client_id} onChange={chooseClient} placeholder="Seleccionar cliente" testid="wo-client-select" options={clients.map((c) => ({ value: c.id, label: c.name }))} /></div>
                <Button type="button" variant="outline" size="icon" className="h-11 w-11" onClick={() => setDlg("client")} data-testid="wo-new-client-button"><Plus className="h-4 w-4" /></Button>
              </div>
            </Field>
            {modality === "proyecto" && <Field label="Proyecto *">
              <div className="flex gap-2">
                <div className="flex-1"><Pick value={f.project_id} onChange={chooseProject} placeholder={f.client_id ? "Seleccionar proyecto" : "Primero elige cliente"} testid="wo-project-select" options={projects.map((p) => ({ value: p.id, label: p.name }))} /></div>
                <Button type="button" variant="outline" size="icon" className="h-11 w-11" disabled={!f.client_id} onClick={() => setDlg("project")} data-testid="wo-new-project-button"><Plus className="h-4 w-4" /></Button>
              </div>
            </Field>}
            <Field label="Sitio">
              <div className="flex gap-2">
                <div className="flex-1"><Pick value={f.site_id} onChange={chooseSite} nullable={f.client_id ? (sites.length ? "Sin sitio" : "El cliente no tiene sitios") : "Primero elige cliente"} testid="wo-site-select" options={sites.map((s) => ({ value: s.id, label: s.name }))} /></div>
                <Button type="button" variant="outline" size="icon" className="h-11 w-11" disabled={!f.client_id} onClick={() => setDlg("site")} data-testid="wo-new-site-button"><Plus className="h-4 w-4" /></Button>
              </div>
            </Field>
            <Field label="Dirección del servicio" className="sm:col-span-2"><Input className="h-11" value={f.service_address} onChange={(e) => set("service_address", e.target.value)} data-testid="wo-address-input" /></Field>
            <Field label="Contacto"><Input className="h-11" value={f.contact_name} onChange={(e) => set("contact_name", e.target.value)} data-testid="wo-contact-name-input" /></Field>
            <Field label="Teléfono"><Input className="h-11" type="tel" value={f.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} data-testid="wo-contact-phone-input" /></Field>
            <Field label="Correo"><Input className="h-11" type="email" value={f.contact_email} onChange={(e) => set("contact_email", e.target.value)} data-testid="wo-contact-email-input" /></Field>
            <Field label="Ambiente"><Pick value={f.environment} onChange={(v) => set("environment", v)} testid="wo-environment-select" options={(meta?.environments || []).map((e) => ({ value: e.code, label: e.label }))} /></Field>
            <Field label="Zona del servicio"><Pick value={f.zone_id} onChange={(v) => set("zone_id", v)} nullable="Sin zona" testid="wo-zone-select" options={zones.filter((z) => z.active || z.id === f.zone_id).map((z) => ({ value: z.id, label: z.path }))} /></Field>
          </div>
        </Section>
        <Section n="02" title="Servicio">
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Unidad de negocio *"><Pick value={f.business_unit_code} onChange={(v) => set("business_unit_code", v)} placeholder="Seleccionar" testid="wo-bu-select" options={(meta?.business_units || []).map((b) => ({ value: b.code, label: b.name }))} /></Field>
            <Field label="Tipo de servicio *"><Pick value={f.service_type_code} onChange={(v) => set("service_type_code", v)} placeholder="Seleccionar" testid="wo-service-type-select" options={movementOptions(meta?.service_types, f.business_unit_code, f.service_type_code).map((s) => ({ value: s.code, label: s.name }))} /></Field>
            <Field label="Prioridad"><Pick value={f.priority} onChange={(v) => set("priority", v)} testid="wo-priority-select" options={(meta?.priorities || []).map((p) => ({ value: p.code, label: p.label }))} /></Field>
            <Field label="Checklist">
              <Pick value={f.checklist_template_id} onChange={(v) => set("checklist_template_id", v)} nullable="Automático (según unidad y servicio)" testid="wo-checklist-select"
                options={[{ value: "none", label: "Sin checklist" }, ...templates.filter((t) => t.active && t.business_unit_code === f.business_unit_code).map((t) => ({ value: t.id, label: t.name }))]} />
            </Field>
            <Field label="Garantía" className="sm:col-span-2">
              {!wopt ? <p className="py-3 text-sm text-muted-foreground">Selecciona unidad y tipo de servicio</p>
                : wopt.fixed ? <p className="py-3 text-sm text-primary" data-testid="wo-warranty-fixed">Según política específica: {wopt.months} meses</p>
                : <Pick value={f.warranty_months_selected === null || f.warranty_months_selected === undefined ? null : String(f.warranty_months_selected)} onChange={(v) => set("warranty_months_selected", v === null ? null : Number(v))}
                    nullable={wopt.months ? `Según política de la unidad (${wopt.months} meses)` : "Por definir al cierre"} testid="wo-warranty-select"
                    options={wopt.options.map((m) => ({ value: String(m), label: m === 0 ? "Sin garantía" : `${m} ${m === 1 ? "mes" : "meses"}` }))} />}
            </Field>
            <Field label="Descripción / alcance del trabajo" className="sm:col-span-3"><Textarea rows={3} value={f.scope} onChange={(e) => set("scope", e.target.value)} data-testid="wo-scope-input" /></Field>
            <Field label="Observaciones para el técnico" className="sm:col-span-3"><Textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="wo-notes-input" /></Field>
          </div>
          {!id && <ExtraServiceDrafts list={extras} onChange={setExtras} templates={templates} />}
        </Section>
        {!isGps && <Section n="03" title="Equipamiento instalado / entregado">
          {modality === "proyecto" && planned.length > 0 && (
            <Button type="button" variant="outline" className="mb-3" data-testid="wo-load-planned-button" onClick={() => setDlg("planned")}>
              Cargar equipamiento planeado ({planned.length})
            </Button>
          )}
          <Dialog open={dlg === "planned"} onOpenChange={(o) => !o && setDlg(null)}>
            <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="wo-planned-preview-dialog">
              <DialogHeader><DialogTitle className="font-display">Cargar equipamiento planeado</DialogTitle></DialogHeader>
              <p className="text-sm text-muted-foreground">Se copiarán {planned.length} partidas del proyecto a esta OS como snapshot independiente{lines.length ? " y reemplazarán el equipamiento capturado en la OS" : ""}. Cambios posteriores en el proyecto no afectarán la OS.</p>
              <EquipmentReadList lines={planned} />
              <DialogFooter>
                <Button variant="ghost" onClick={() => setDlg(null)} data-testid="wo-planned-cancel-button">Cancelar</Button>
                <Button data-testid="wo-planned-confirm-button" onClick={() => { setLines(planned.map((l, i) => ({ ...l, line_id: null, key: `pl-${i}-${Date.now()}` }))); setDlg(null); toast.success("Equipamiento planeado copiado a la OS"); }}>Confirmar y copiar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <EquipmentEditor lines={lines} onChange={setLines} buCode={f.business_unit_code} allowDescription allowManual workOrderId={id} />
        </Section>}
        {isGps && schedule}
        {isGps && (
          <Section n="03B" title="Unidades GPS">
            {id ? (gps && <><p className="mb-3 text-xs text-muted-foreground">Cada unidad se guarda por separado con su botón “Guardar unidad”.</p><GpsUnitsEditor woId={id} gps={gps} editable serviceType={f.service_type_code} technicianId={f.technician_id} /></>)
              : <GpsUnitsEditor units={units} onChange={setUnits} editable serviceType={f.service_type_code} technicianId={f.technician_id} />}
          </Section>
        )}
        <Section n={isGps ? "04" : "02B"} title="Origen comercial y finanzas"><FinanceSections f={f} set={set} /></Section>
        {!isGps && schedule}
        <div className="flex justify-end gap-2 pb-8">
          <Button variant="ghost" onClick={() => nav(-1)}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} className="h-11 px-8" data-testid="wo-save-button">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{id ? "Guardar cambios" : "Crear OS"}</Button>
        </div>
      </div>
      <FormDialog open={dlg === "client"} onOpenChange={(o) => !o && setDlg(null)} title="Nuevo cliente" fields={clientFields} onSubmit={saveClient} testid="quick-client" />
      <FormDialog open={dlg === "site"} onOpenChange={(o) => !o && setDlg(null)} title="Nuevo sitio" fields={siteFields} onSubmit={saveSite} testid="quick-site" />
      <FormDialog open={dlg === "project"} onOpenChange={(o) => !o && setDlg(null)} title="Nuevo proyecto" fields={projectFields(meta, zones)} initial={{ business_unit_code: meta?.business_units?.[0]?.code }} onSubmit={saveProject} testid="quick-project" />
    </div>
  );
}
